/**
 * Wegklikken van een wachtende fan (20-10-2026, Jordy: "per ongeluk verliezen van traffic").
 *
 * - `WegklikBevestig`: eerst vragen, dan pas sluiten. Eén klik op de × laat de fan
 *   niet meer ongemerkt verdwijnen; tijdens het versturen staat de knop uit, dus een
 *   dubbele klik sluit niet twee keer (de server negeert een herhaling ook).
 * - `WeggekliktLijst`: owner/admin zien wie wat wanneer wegklikte en zetten het
 *   terug in de wachtrij. Alles staat in audit_logs.
 * Gedeelde Modal: onder 900 px schermvullend (mobiel.css).
 */
import { useState } from 'react'
import { Modal } from './Modal'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useActions } from '../lib/actions'
import { dtShort } from '../lib/format'

export function WegklikBevestig({ id, onClose, onDone }: { id: number; onClose: () => void; onDone?: () => void }) {
  const { notify } = useActions()
  const [bezig, setBezig] = useState(false)
  const sluit = async () => {
    if (bezig) return
    setBezig(true)
    try {
      await api.post(`/api/admin/conversations/${id}/dismiss-unanswered`, {})
      notify('Removed from Waiting. It comes back when the fan writes again.')
      onDone?.()
      onClose()
    } catch (e) {
      notify(`Could not remove: ${e instanceof Error ? e.message : String(e)}`, false)
      setBezig(false)
    }
  }
  return (
    <Modal title="Remove this chat from Waiting?" onClose={onClose}
           desc="The fan is waiting for a reply. The chat comes back as soon as the fan writes again. Owners and admins can put it back under 'Closed chats'.">
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }} data-testid="wegklik-bevestig">
        <button className="btn ghost" type="button" autoFocus disabled={bezig} onClick={onClose}>Cancel</button>
        <button className="btn" type="button" disabled={bezig} data-testid="wegklik-ja"
                onClick={() => void sluit()}>{bezig ? 'Removing…' : 'Remove from Waiting'}</button>
      </div>
    </Modal>
  )
}

interface Weg {
  id: number; platform: string; stage_name: string; fan_display_name: string
  dismissed_at: string | null; dismissed_by: string | null; fan_message_at: string | null
  /** false = gesloten vóór het logboek (20-10-2026): wie en wanneer zijn niet bewaard. */
  logged?: boolean
}

export function WeggekliktLijst({ onClose, onDone }: { onClose: () => void; onDone?: () => void }) {
  const q = useApi<{ conversations: Weg[] }>('/api/admin/conversations/panels/dismissed')
  const { notify } = useActions()
  const [bezig, setBezig] = useState<number | null>(null)
  const rijen = q.data?.conversations ?? []
  const herstel = async (id: number) => {
    if (bezig !== null) return
    setBezig(id)
    try {
      await api.post(`/api/admin/conversations/${id}/restore-dismissed`, {})
      notify('Put back in the Waiting queue.')
      q.refetch(); onDone?.()
    } catch (e) {
      notify(`Restore failed: ${e instanceof Error ? e.message : String(e)}`, false)
    } finally {
      setBezig(null)
    }
  }
  return (
    <Modal title="Closed chats" onClose={onClose}
           desc="Waiting fans that someone removed from the queue (last 14 days) and that are still hidden. A fan who writes again is already back in Waiting.">
      {q.loading && !q.data ? <div className="mut2">Loading…</div> : null}
      {q.data && !rijen.length ? <div className="mut2">No closed chats.</div> : null}
      <div style={{ display: 'grid', gap: 6 }} data-testid="weggeklikt-lijst">
        {rijen.map((r) => (
          <div key={r.id} className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 200 }}>
              <b>{r.fan_display_name}</b> · {r.stage_name} ({r.platform === 'f2f' ? 'F2F' : 'FC'})
              <span className="mut2" style={{ display: 'block', fontSize: 12 }}>
                {r.logged === false
                  ? `Closed earlier (who and when not recorded) · fan wrote ${dtShort(r.fan_message_at)}`
                  : `Closed by ${r.dismissed_by} · ${dtShort(r.dismissed_at)}`}
              </span>
            </span>
            <a className="btn ghost sm" href={`/conversations/show/${r.id}`}>Open</a>
            <button className="btn sm" type="button" disabled={bezig !== null} data-testid="weggeklikt-herstel"
                    onClick={() => void herstel(r.id)}>{bezig === r.id ? 'Restoring…' : 'Put back'}</button>
          </div>
        ))}
      </div>
    </Modal>
  )
}
